import { connect } from 'react-redux';
import React, { Component, createRef } from 'react';
import { compose } from 'redux';
import { Location } from 'history';
import { NextRouter } from 'next/router';

import {
  getAllCollections,
  getFlattenedSubNavigationTypes,
} from '../../../store/selectors/collections.selectors';
import { getPageComponents } from '../../../store/selectors/page.selectors';
import { setNavItems } from '../../../store/actions/ui.actions';

import CallToAction from '../CallToAction';
import LinkAdapter from '../LinkAdapter';

import { default as styles } from '../../../scss/components/AnchorNav.module.scss';
import withHeaderPortal from '../../../common/WithHeaderPortal';
import { getLocationFields } from '../../../../../utils/Utils';
import { withCssModulesClassNames } from '../../../common/nextMigrationHelpers';
import { NavItem } from '../../../store/reducers/ui.reducer.types';

// TODO: when converting this file to a functional component, please use the useStickElementOnScroll instead of the withHeaderPortal HOC. Example is available at FilterNav

const withCssModulesClassNamesHandler = withCssModulesClassNames(styles);

export type AnchorNavProps = {
  isPDP: boolean;
  setRef: () => void;
  collections: { id: string; }[];
  location: Location | NextRouter;
  activeCollectionID: string;
  onItemClick: () => void;
  setNavItemsAction: (navItems: NavItem[] | null) => void;
  collectionsSubNavTypes: string[];
  components: {
    type?: string;
    header?: string;
    anchor?: string;
  }[];
};

export class AnchorNav extends Component<AnchorNavProps, { activeCollectionIdHash: string }> {
  // disabling Property 'navItems' has no initializer and is not definitely assigned in the constructor.
  // @ts-ignore
  navItems: NavItem[];

  // disabling Property 'prevNavItems' has no initializer and is not definitely assigned in the constructor.
  // @ts-ignore
  prevNavItems: NavItem[];

  navItemsContainerRef: React.RefObject<HTMLDivElement>;

  static defaultProps = {
    isPDP: false,
    setRef: (): null => null,
    setNavItemsAction: (): null => null,
  };

  constructor(props: AnchorNavProps) {
    super(props);
    this.navItemsContainerRef = createRef();

    this.setNavItems = this.setNavItems.bind(this);
    this.shouldRender = this.shouldRender.bind(this);

    this.setNavItems(props);
    this.state = {
      activeCollectionIdHash: getLocationFields(props.location).hash,
    };
  }

  static isComponentAnAnchor({ type, anchor, header }:  { anchor?: string; header?: string, type?: string } | undefined = {}): boolean {
    return (
      typeof anchor === 'string' && anchor.length > 0
      && typeof header === 'string' && header.length > 0
      && typeof type === 'string' && type !== 'AnchorNav'
    );
  }

  static mapComponentToNavItem({ anchor, header }: { anchor?: string; header?: string } | undefined = {}): NavItem {
    return {
      id: anchor,
      slug: anchor,
      subHeader: header,
    };
  }

  static getNavItems(nextProps: AnchorNavProps) {
    const {
      isPDP,
      collections = [],
      components,
    } = nextProps;
    const navItems = isPDP
      ? components
        .filter(AnchorNav.isComponentAnAnchor)
        .map(AnchorNav.mapComponentToNavItem)
      : collections;

    return navItems.length > 1 ? navItems : [];
  }

  setNavItems(nextProps: AnchorNavProps) {
    this.navItems = AnchorNav.getNavItems(nextProps);
  }

  shouldRender() {
    const {
      collectionsSubNavTypes,
    } = this.props;

    return this.navItems?.length && !collectionsSubNavTypes?.includes('None');
  }

  scrollToViewport() {
    const { children: navigationLinks } = this.navItemsContainerRef.current?.children[0] ?? {};

    if (!navigationLinks) {
      return;
    }

    const links = [...navigationLinks];
    const activeLink = links.find((link) => link.classList.contains('active'));

    if (!activeLink) {
      return;
    }

    const rect = activeLink.getBoundingClientRect();

    if (rect.left <= 0 || rect.right >= document.documentElement.clientWidth) {
      this.navItemsContainerRef.current?.scrollBy({
        left: rect.left,
        behavior: 'smooth',
      });
    }
  }

  handleHashChange = (newUrlPath: string) => {
    this.setState({
      activeCollectionIdHash: newUrlPath.length > 0
        ? new URL(newUrlPath, window.location.origin).hash : '',
    });
  };

  shouldComponentUpdate(nextProps: AnchorNavProps) {
    const {
      isPDP,
      collections,
      components,
    } = this.props;
    const {
      isPDP: nextIsPDP,
      collections: nextCollections,
      components: nextComponents,
    } = nextProps;

    if (
      nextIsPDP !== isPDP
      || (nextIsPDP && nextComponents !== components)
      || (!nextIsPDP && (
        nextCollections?.length !== collections?.length
        || !nextCollections.every((collection, i) => collection.id === collections[i].id)
      ))
    ) {
      this.setNavItems(nextProps);
    }
    return true;
  }

  componentDidMount() {
    const {
      location,
      setNavItemsAction,
    } = this.props;

    if (this.navItems?.length) {
      this.prevNavItems = this.navItems;
      setNavItemsAction(this.navItems);
    }

    if ('events' in location) {
      location.events.on('hashChangeComplete', this.handleHashChange);
    }
  }

  componentDidUpdate(prevProps: AnchorNavProps) {
    const {
      setNavItemsAction,
      location,
    } = this.props;

    const { hash } = getLocationFields(location);
    const { hash: prevHash } = getLocationFields(prevProps.location);

    if (hash && hash !== prevHash) {
      this.scrollToViewport();
    }

    if (this.prevNavItems !== this.navItems) {
      this.prevNavItems = this.navItems;
      setNavItemsAction(this.navItems);
    }
  }

  componentWillUnmount() {
    const {
      location,
      setNavItemsAction,
    } = this.props;

    if (this.navItems?.length) {
      setNavItemsAction(null);
    }

    if ('events' in location) {
      location.events.off('hashChangeComplete', this.handleHashChange);
    }
  }

  render() {
    const {
      location,
      isPDP,
    } = this.props;

    if (!this.shouldRender()) {
      return null;
    }

    const nextApp = 'asPath' in location;
    const { search, pathname } = getLocationFields(location);

    return (
      <div className={withCssModulesClassNamesHandler('anchor-nav', { 'anchor-nav--pdp': isPDP })}>
        <div className={withCssModulesClassNamesHandler('anchor-nav-scroll')} ref={this.navItemsContainerRef} data-testid="anchorNavScroll">
          <div className={withCssModulesClassNamesHandler('anchor-nav-items')}>
            {this.navItems.map((item) => {
              const {
                slug,
                subHeader,
                callToAction,
              } = item;

              const urlToReplace = `${pathname}${search}#${slug}`;
              // eslint-disable-next-line react/destructuring-assignment
              const active = slug === this.state.activeCollectionIdHash.slice(1);

              return (
                <LinkAdapter
                  data-testid="navLink"
                  key={slug}
                  nextApp={nextApp}
                  href={urlToReplace}
                  className={withCssModulesClassNamesHandler('selector', { active })}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(nextApp ? { scroll: false, shallow: true, as: urlToReplace } : {})}
                >
                  {subHeader}
                  <div>
                    {callToAction && <CallToAction callToAction={callToAction} />}
                  </div>
                </LinkAdapter>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  collections: getAllCollections(state as never),
  collectionsSubNavTypes: getFlattenedSubNavigationTypes(state),
  components: getPageComponents(state),
});

const mapDispatchToProps = {
  setNavItemsAction: setNavItems,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withHeaderPortal,
)(AnchorNav);
